import * as THREE from 'three';
import {
  face_vert,
  line_vert,
  mouse_vert,
  advection_frag,
  externalForce_frag,
  divergence_frag,
  poisson_frag,
  pressure_frag,
  viscous_frag
} from './Shaders';

type Uniforms = Record<string, { value: any }>;

export class ShaderPass {
  props: any;
  uniforms?: Uniforms;
  scene: THREE.Scene | null = null;
  camera: THREE.Camera | null = null;
  material: THREE.RawShaderMaterial | null = null;
  geometry: THREE.BufferGeometry | null = null;
  plane: THREE.Mesh | null = null;

  constructor(props: any) {
    this.props = props || {};
    this.uniforms = this.props.material?.uniforms;
  }

  init(..._args: any[]) {
    this.scene = new THREE.Scene();
    this.camera = new THREE.Camera();
    if (this.uniforms) {
      this.material = new THREE.RawShaderMaterial(this.props.material);
      this.geometry = new THREE.PlaneGeometry(2, 2);
      this.plane = new THREE.Mesh(this.geometry, this.material);
      this.scene.add(this.plane);
    }
  }

  update(..._args: any[]) {
    if (!(this as any).Common?.renderer || !this.scene || !this.camera) return;
    (this as any).Common.renderer.setRenderTarget(this.props.output || null);
    (this as any).Common.renderer.render(this.scene, this.camera);
    (this as any).Common.renderer.setRenderTarget(null);
  }

  // Will be injected during initialization

}

export class Advection extends ShaderPass {
  line!: THREE.LineSegments;
  constructor(simProps: any) {
    super({
      material: {
        vertexShader: face_vert,
        fragmentShader: advection_frag,
        uniforms: {
          boundarySpace: { value: simProps.cellScale },
          px: { value: simProps.cellScale },
          fboSize: { value: simProps.fboSize },
          velocity: { value: simProps.src.texture },
          dt: { value: simProps.dt },
          isBFECC: { value: true }
        }
      },
      output: simProps.dst
    });
    this.uniforms = this.props.material.uniforms;
    this.init();
  }

  init() {
    super.init();
    this.createBoundary();
  }

  createBoundary() {
    const boundaryG = new THREE.BufferGeometry();
    const vertices_boundary = new Float32Array([
      -1, -1, 0, -1, 1, 0, -1, 1, 0, 1, 1, 0, 1, 1, 0, 1, -1, 0, 1, -1, 0, -1, -1, 0
    ]);
    boundaryG.setAttribute('position', new THREE.BufferAttribute(vertices_boundary, 3));
    const boundaryM = new THREE.RawShaderMaterial({
      vertexShader: line_vert,
      fragmentShader: advection_frag,
      uniforms: this.uniforms!
    });
    this.line = new THREE.LineSegments(boundaryG, boundaryM);
    this.scene!.add(this.line);
  }

  update(...args: any[]) {
    const { dt, isBounce, BFECC } = (args[0] || {}) as { dt?: number; isBounce?: boolean; BFECC?: boolean };
    if (!this.uniforms) return;
    if (typeof dt === 'number') this.uniforms.dt.value = dt;
    if (typeof isBounce === 'boolean') this.line.visible = isBounce;
    if (typeof BFECC === 'boolean') this.uniforms.isBFECC.value = BFECC;
    super.update();
  }
}

export class ExternalForce extends ShaderPass {
  mouse!: THREE.Mesh;
  constructor(simProps: any) {
    super({ output: simProps.dst });
    this.init(simProps);
  }

  init(simProps: any) {
    super.init();
    const mouseG = new THREE.PlaneGeometry(1, 1);
    const mouseM = new THREE.RawShaderMaterial({
      vertexShader: mouse_vert,
      fragmentShader: externalForce_frag,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      uniforms: {
        px: { value: simProps.cellScale },
        force: { value: new THREE.Vector2(0, 0) },
        center: { value: new THREE.Vector2(0, 0) },
        scale: { value: new THREE.Vector2(simProps.cursor_size, simProps.cursor_size) }
      }
    });
    this.mouse = new THREE.Mesh(mouseG, mouseM);
    this.scene!.add(this.mouse);
  }

  update(...args: any[]) {
    const props = args[0] || {};
    const Mouse = (this as any).Mouse;
    if (!Mouse) return;

    const forceX = (Mouse.diff.x / 2) * (props.mouse_force || 0);
    const forceY = (Mouse.diff.y / 2) * (props.mouse_force || 0);
    const cellScale = props.cellScale || { x: 1, y: 1 };
    const cursorSize = props.cursor_size || 0;
    const cursorSizeX = cursorSize * cellScale.x;
    const cursorSizeY = cursorSize * cellScale.y;
    const centerX = Math.min(
      Math.max(Mouse.coords.x, -1 + cursorSizeX + cellScale.x * 2),
      1 - cursorSizeX - cellScale.x * 2
    );
    const centerY = Math.min(
      Math.max(Mouse.coords.y, -1 + cursorSizeY + cellScale.y * 2),
      1 - cursorSizeY - cellScale.y * 2
    );
    const uniforms = (this.mouse.material as THREE.RawShaderMaterial).uniforms;
    uniforms.force.value.set(forceX, forceY);
    uniforms.center.value.set(centerX, centerY);
    uniforms.scale.value.set(cursorSize, cursorSize);
    super.update();
  }
}

export class Viscous extends ShaderPass {
  constructor(simProps: any) {
    super({
      material: {
        vertexShader: face_vert,
        fragmentShader: viscous_frag,
        uniforms: {
          boundarySpace: { value: simProps.boundarySpace },
          velocity: { value: simProps.src.texture },
          velocity_new: { value: simProps.dst_.texture },
          v: { value: simProps.viscous },
          px: { value: simProps.cellScale },
          dt: { value: simProps.dt }
        }
      },
      output: simProps.dst,
      output0: simProps.dst_,
      output1: simProps.dst
    });
    this.init();
  }

  update(...args: any[]) {
    const { viscous, iterations, dt } = (args[0] || {}) as { viscous?: number; iterations?: number; dt?: number };
    if (!this.uniforms) return;
    let fbo_in: any, fbo_out: any;
    if (typeof viscous === 'number') this.uniforms.v.value = viscous;
    const iter = iterations ?? 0;
    for (let i = 0; i < iter; i++) {
      if (i % 2 === 0) {
        fbo_in = this.props.output0;
        fbo_out = this.props.output1;
      } else {
        fbo_in = this.props.output1;
        fbo_out = this.props.output0;
      }
      this.uniforms.velocity_new.value = fbo_in.texture;
      this.props.output = fbo_out;
      if (typeof dt === 'number') this.uniforms.dt.value = dt;
      super.update();
    }
    return fbo_out;
  }
}

export class Divergence extends ShaderPass {
  constructor(simProps: any) {
    super({
      material: {
        vertexShader: face_vert,
        fragmentShader: divergence_frag,
        uniforms: {
          boundarySpace: { value: simProps.boundarySpace },
          velocity: { value: simProps.src.texture },
          px: { value: simProps.cellScale },
          dt: { value: simProps.dt }
        }
      },
      output: simProps.dst
    });
    this.init();
  }

  update(...args: any[]) {
    const { vel } = (args[0] || {}) as { vel?: any };
    if (this.uniforms && vel) {
      this.uniforms.velocity.value = vel.texture;
    }
    super.update();
  }
}

export class Poisson extends ShaderPass {
  constructor(simProps: any) {
    super({
      material: {
        vertexShader: face_vert,
        fragmentShader: poisson_frag,
        uniforms: {
          boundarySpace: { value: simProps.boundarySpace },
          pressure: { value: simProps.dst_.texture },
          divergence: { value: simProps.src.texture },
          px: { value: simProps.cellScale }
        }
      },
      output: simProps.dst,
      output0: simProps.dst_,
      output1: simProps.dst
    });
    this.init();
  }

  update(...args: any[]) {
    const { iterations } = (args[0] || {}) as { iterations?: number };
    let p_in: any, p_out: any;
    const iter = iterations ?? 0;
    for (let i = 0; i < iter; i++) {
      if (i % 2 === 0) {
        p_in = this.props.output0;
        p_out = this.props.output1;
      } else {
        p_in = this.props.output1;
        p_out = this.props.output0;
      }
      if (this.uniforms) this.uniforms.pressure.value = p_in.texture;
      this.props.output = p_out;
      super.update();
    }
    return p_out;
  }
}

export class Pressure extends ShaderPass {
  constructor(simProps: any) {
    super({
      material: {
        vertexShader: face_vert,
        fragmentShader: pressure_frag,
        uniforms: {
          boundarySpace: { value: simProps.boundarySpace },
          pressure: { value: simProps.src_p.texture },
          velocity: { value: simProps.src_v.texture },
          px: { value: simProps.cellScale },
          dt: { value: simProps.dt }
        }
      },
      output: simProps.dst
    });
    this.init();
  }

  update(...args: any[]) {
    const { vel, pressure } = (args[0] || {}) as { vel?: any; pressure?: any };
    if (this.uniforms && vel && pressure) {
      this.uniforms.velocity.value = vel.texture;
      this.uniforms.pressure.value = pressure.texture;
    }
    super.update();
  }
}
